import { PROBLEMS, INDUSTRIES, USER_TYPES, getProductKB } from "../data/knowledge";
import { FEATURE_CATEGORIES, featurePath } from "../data/features";

// Product Finder matching. Recommendations come only from the problem ->
// product relationships in knowledge.js (and the capabilities they cite), so
// a product is suggested because it solves a stated problem - never because
// a generic word like "automation" happens to appear in the query.

const STOPWORDS = new Set(
  "i me my we our you your a an the and or for to of in on at with is are be it this that need needs want wants looking help how can do does software tool tools app platform solution solutions business businesses automate automation automatic system something some get manage".split(
    " "
  )
);

// Hyphens become spaces so "QR-based" matches "qr based" and "qr".
const normalise = (s) => ` ${s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim()} `;

// Multi-word phrases match as phrases; single words match whole words only,
// with a light plural tolerance ("review" ~ "reviews").
function phraseHits(text, phrase) {
  const p = normalise(phrase).trim();
  if (p.includes(" ")) return text.includes(` ${p} `) ? 2 : 0;
  if (STOPWORDS.has(p)) return 0;
  if (text.includes(` ${p} `)) return 1;
  if (p.endsWith("s") && text.includes(` ${p.slice(0, -1)} `)) return 1;
  if (text.includes(` ${p}s `)) return 1;
  return 0;
}

const featureFor = (productId) => {
  const kb = getProductKB(productId);
  return kb?.featureId ? FEATURE_CATEGORIES.find((c) => c.id === kb.featureId) : null;
};

export const productUrl = (productId) => {
  const f = featureFor(productId);
  return f ? featurePath(f) : "/pricing";
};

export function detectContext(query) {
  const text = normalise(query);
  const industry = INDUSTRIES.find((ind) => ind.keywords.some((k) => phraseHits(text, k)));
  const userType = USER_TYPES.find((u) => u.keywords.some((k) => phraseHits(text, k)));
  return { industry: industry?.id || null, userType: userType?.id || null };
}

// Returns { problems: [{problem, score}], products: [...], context }.
// `industryId` / `userTypeId` may come from explicit pickers; otherwise they
// are detected from the text.
export function findSolutions({ query = "", industryId = null, userTypeId = null, limit = 3 } = {}) {
  const text = normalise(query);
  const detected = detectContext(query);
  const industry = INDUSTRIES.find((i) => i.id === (industryId || detected.industry)) || null;
  const userType = USER_TYPES.find((u) => u.id === (userTypeId || detected.userType)) || null;

  const scored = PROBLEMS.map((problem) => {
    let score = 0;
    for (const ph of problem.phrases) score += phraseHits(text, ph);
    return { problem, score };
  })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);

  // Aggregate products across the matched problems, keeping the reasons.
  const byProduct = new Map();
  scored.forEach(({ problem, score }, rank) => {
    problem.solvedBy.forEach((s, i) => {
      const kb = getProductKB(s.product);
      if (!kb || kb.status !== "ACTIVE") return;
      const entry = byProduct.get(s.product) || { product: kb, score: 0, reasons: [], capabilities: new Set() };
      // Earlier solvers of a problem are its primary answer.
      entry.score += score * (i === 0 ? 3 : 2) - rank * 0.1;
      entry.reasons.push({ problem: problem.title, role: s.role });
      s.capabilities.forEach((c) => entry.capabilities.add(c));
      byProduct.set(s.product, entry);
    });
  });

  // Industry context nudges products that fit that industry - it never adds
  // a product on its own when the user described a problem.
  if (industry) {
    for (const entry of byProduct.values()) {
      if (industry.products.includes(entry.product.id)) entry.score += 1;
    }
  }

  let products = [...byProduct.values()].sort((a, b) => b.score - a.score);

  // Only an industry / user type and no problem text: fall back to the
  // industry's common products, labelled as such.
  let basis = "problem";
  if (products.length === 0 && industry) {
    basis = "industry";
    products = industry.products
      .map((id) => getProductKB(id))
      .filter((kb) => kb && kb.status === "ACTIVE")
      .map((kb) => ({ product: kb, score: 0, reasons: [{ problem: industry.name, role: industry.why }], capabilities: new Set() }));
  } else if (products.length === 0 && userType && ["agency", "freelancer", "entrepreneur"].includes(userType.id)) {
    basis = "userType";
    const agencyProblem = PROBLEMS.find((p) => p.id === (userType.id === "agency" ? "many-clients" : "resell-own-brand"));
    products = agencyProblem.solvedBy.map((s) => ({
      product: getProductKB(s.product),
      score: 0,
      reasons: [{ problem: agencyProblem.title, role: s.role }],
      capabilities: new Set(s.capabilities),
    }));
  }

  const results = products.slice(0, limit).map((e) => {
    const feature = featureFor(e.product.id);
    const points = feature ? feature.points.filter((p) => e.capabilities.has(p.title)) : [];
    return {
      id: e.product.id,
      name: e.product.name,
      definition: e.product.definition,
      url: productUrl(e.product.id),
      reasons: e.reasons,
      features: points.length ? points : feature ? feature.points.slice(0, 3) : [],
      related: e.product.related.map((id) => getProductKB(id)).filter(Boolean),
    };
  });

  return { problems: scored.map((s) => s.problem), products: results, industry, userType, basis };
}

// Keyword search over the FAQ knowledge base, so the finder also surfaces
// direct answers (with a link to the FAQ section they live in).
export function searchFaqs(query, segments, limit = 4) {
  const tokens = normalise(query)
    .trim()
    .split(" ")
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));
  if (tokens.length === 0) return [];
  const hits = [];
  segments.forEach((seg) =>
    seg.items.forEach((it) => {
      const text = normalise(`${it.q} ${it.a}`);
      const q = normalise(it.q);
      let score = 0;
      tokens.forEach((t) => {
        if (q.includes(` ${t}`)) score += 2;
        else if (text.includes(` ${t}`)) score += 1;
      });
      if (score > 0) hits.push({ ...it, segmentId: seg.id, segmentTitle: seg.title, score });
    })
  );
  return hits.sort((a, b) => b.score - a.score).slice(0, limit);
}
